import { CRYPTO_SECRET_KEY } from 'config/app.config';
import CryptoJS from 'crypto-js';
import { mobileNumberLength } from 'helpers/ValidationHelper';

import type { ReactNode } from 'react';
export const encryptObject = (obj: object): string => {
  const convertedObj = JSON.stringify(obj);
  return CryptoJS.AES.encrypt(convertedObj, CRYPTO_SECRET_KEY).toString();
};

export const decryptObject = (obj: string): object => {
  return JSON.parse(CryptoJS.AES.decrypt(obj, CRYPTO_SECRET_KEY).toString(CryptoJS.enc.Utf8));
};

export const encryptText = (message: string): string => {
  message = message.toString();
  const cipherText = CryptoJS.AES.encrypt(message, CRYPTO_SECRET_KEY);
  return encodeURIComponent(cipherText.toString());
};

export const decryptText = (message: string): string => {
  const bytes = CryptoJS.AES.decrypt(decodeURIComponent(message), CRYPTO_SECRET_KEY);
  return bytes.toString(CryptoJS.enc.Utf8);
};
export const generateRandomNumber = (): number => {
  // return Date.now() * Math.random();
  return Date.now();
};
export const maskEmail = (email: string) => {
  if (!email) return '';

  const [name, domain] = email.split('@');

  if (name.length <= 2) {
    return name[0] + '*@' + domain;
  }

  const first = name[0];
  const last = name[name.length - 1];
  const masked = '*'.repeat(name.length - 2);

  return `${first}${masked}${last}@${domain}`;
};
export const getStringPrefix = (name: string): string => {
  const nameArr = name.split(' ').filter(Boolean);
  let prefix = '';

  if (nameArr.length > 0) {
    prefix = nameArr[0][0];
    if (nameArr.length > 1) {
      let lastWordIndex = nameArr.length - 1;
      while (lastWordIndex > 0 && /[^a-zA-Z0-9]/.test(nameArr[lastWordIndex][0])) {
        lastWordIndex--;
      }
      if (lastWordIndex > 0) {
        prefix += nameArr[lastWordIndex][0];
      } else {
        prefix += nameArr[0][0];
      }
    }
  }

  // Return the capitalized prefix
  return capitalize(prefix);
};
export function getTruncatedString(
  stringVal: string,
  length = 20,
  postfix: string | ReactNode = '...'
) {
  if (stringVal) {
    if (stringVal.length > length) {
      const truncatedVal = stringVal.substring(0, length);
      if (typeof postfix === 'string') {
        stringVal = truncatedVal + postfix;
      } else {
        return [truncatedVal, postfix];
      }
    }
    return stringVal;
  }
  return '';
}
export const getFullName = (firstName: string, lastName: string) => {
  if (firstName && lastName) {
    return capitalizeWordPrefix(firstName + ' ' + lastName);
  } else if (firstName) {
    return capitalizeWordPrefix(firstName);
  } else if (lastName) {
    return capitalizeWordPrefix(lastName);
  } else {
    return '';
  }
};
export { getFriendlyFieldMessage, humanizeFieldName } from './ErrorMessageHelper';

export const capitalizeWordPrefix = (text: string): string => {
  if (!text) return '';
  const words = text.split(' ');
  for (let i = 0; i < words.length; i++) {
    if (words[i][0]) {
      words[i] = words[i][0].toUpperCase() + words[i].substr(1);
    }
  }
  return words.join(' ');
};
// "SHOOTERS SPORTS ARMORY" -> "Shooters Sports Armory" (FFL licence names come in all caps)
export const toTitleCase = (text?: string | null) =>
  capitalizeWordPrefix(String(text ?? '').toLowerCase());

// An FFL name as shown on order screens: each word capitalised, cut at 15 letters + "..."
export const FFL_NAME_DISPLAY_LENGTH = 15;
export const formatFFLDisplayName = (name?: string | null) =>
  getTruncatedString(toTitleCase(name), FFL_NAME_DISPLAY_LENGTH);

// "Street, City, State Zip" from an FFL licence's premise address; missing parts are skipped, so
// an FFL with no address gives '' (never ", ,")
export const getFFLPremiseAddress = (ffl: any) =>
  [
    ffl?.premise_street,
    ffl?.premise_city,
    [ffl?.premise_state, ffl?.premise_zip_code].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

export const capitalize = (string: string) =>
  (string.charAt(0).toUpperCase() + string.slice(1)).replace(/-/g, ' ').replace('_and_', '&');
export const placeParamsToURL = (pathRegex: string, params?: any): string => {
  let replacedString = pathRegex;

  if (params) {
    Object.keys(params).forEach((key: any) => {
      // const placeholderRegex = new RegExp(`:${key}(?![\\w-])`, 'g');
      const placeholderRegex = new RegExp(`:${key}\\??`, 'g');
      replacedString = replacedString.replace(placeholderRegex, params[key]);
    });
  }
  return replacedString;
};
export const getQueryParams = (url: string): object => {
  if (url.indexOf('?') == -1) {
    return {};
  }
  const paramArr = url.slice(url.indexOf('?') + 1).split('&');
  const params: any = {};
  paramArr.map((param) => {
    const [key, val] = param.split('=');
    const queryval = val.replace(/\+/g, '%20');
    params[key] = decodeURIComponent(queryval);
  });
  return params;
};
export const replaceNewlinesWithBR = (text: any) => {
  return text ? text.toString().replace(/\n/g, '<br />') : text;
};
// Add change event listener to elements with a specific class
document.addEventListener('change', function (event) {
  const target = event.target as HTMLInputElement;
  if (target && target.classList.contains('allow_decimal_number_only')) {
    if (target.value) {
      target.value = roundAmount(target.value);
    }
  }
});
export const roundAmount = (value: any) => {
  return parseFloat(value).toFixed(2);
};
/**
 * Strips a phone number down to the digits that are stored and validated.
 * Takes any value: the APIs hand phone numbers back as numbers as well as strings.
 */
export const toMobileDigits = (value: any) =>
  // `!value` also covers the 0 some endpoints send for "no number on file", which
  // would otherwise show up in the field as a lone 0
  !value ? '' : String(value).replace(/\D/g, '').slice(0, mobileNumberLength);
/**
 * Formats a phone number for display: 2313139966 -> 231-313-9966.
 * A dash is only added once the group before it is complete, so backspacing over one
 * removes the digit ahead of it rather than leaving the dash stuck in place.
 */
export const formatMobileNumber = (value: any) => {
  const digits = toMobileDigits(value);
  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};
export const allowNumberOnly = (event: any) => {
  if (
    event.key === 'ArrowLeft' ||
    event.key === 'ArrowRight' ||
    event.key === 'Backspace' ||
    event.key === 'Delete' ||
    event.key === 'Tab' ||
    event.key === 'Escape' ||
    event.key === 'Enter' ||
    (event.key >= '0' && event.key <= '9') ||
    (event.ctrlKey &&
      (event.key === 'c' || event.key === 'v' || event.key === 'x' || event.key === 'a'))
  ) {
    // Allow the keypress
  } else {
    // Prevent the keypress
    event.preventDefault();
  }
};
export const allowDecimalNumberOnly = (event: any) => {
  const value = event.target.value;
  if (
    event.key === 'ArrowLeft' ||
    event.key === 'ArrowRight' ||
    event.key === 'Backspace' ||
    event.key === 'Delete' ||
    event.key === 'Tab' ||
    event.key === 'Escape' ||
    event.key === 'Enter' ||
    (event.key === '.' && value.indexOf('.') === -1) ||
    (event.key >= '0' && event.key <= '9') ||
    (event.ctrlKey &&
      (event.key === 'c' || event.key === 'v' || event.key === 'x' || event.key === 'a'))
  ) {
    // Allow the keypress
    return true;
  } else {
    // Prevent the keypress
    event.preventDefault();
    return false;
  }
};
export const parseLabel = (name: string) => {
  const spaced = name.replace(/[_-]/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
};
export const numberFormatShort = (numberVal: number, precision: number = 0): string => {
  let value = numberVal;
  let suffix = '';

  if (numberVal >= 1_000 && numberVal < 1_000_000) {
    value = numberVal / 1_000;
    suffix = 'K';
  } else if (numberVal >= 1_000_000 && numberVal < 1_000_000_000) {
    value = numberVal / 1_000_000;
    suffix = 'M';
  } else if (numberVal >= 1_000_000_000 && numberVal < 1_000_000_000_000) {
    value = numberVal / 1_000_000_000;
    suffix = 'B';
  } else if (numberVal >= 1_000_000_000_000) {
    value = numberVal / 1_000_000_000_000;
    suffix = 'T';
  } else {
    return numberVal.toString();
  }

  // Remove .0 automatically
  const formatted = value % 1 === 0 ? value.toString() : value.toFixed(precision);

  return formatted + suffix;
};
export function getEmbedUrl(url: string) {
  if (!url) return null;

  // ✅ YouTube
  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    // Shorts
    if (url.includes('/shorts/')) {
      const id = url.split('/shorts/')[1]?.split('?')[0];
      return {
        type: 'youtube',
        url: `https://www.youtube.com/embed/${id}`,
      };
    }

    // Watch
    else if (url.includes('watch?v=')) {
      const id = url.split('watch?v=')[1]?.split('&')[0];
      return {
        type: 'youtube',
        url: `https://www.youtube.com/embed/${id}`,
      };
    }

    // youtu.be
    else if (url.includes('youtu.be/')) {
      const id = url.split('youtu.be/')[1]?.split('?')[0];
      return {
        type: 'youtube',
        url: `https://www.youtube.com/embed/${id}`,
      };
    }
  }

  // ✅ Vimeo
  else if (url.includes('vimeo.com')) {
    const id = url.split('vimeo.com/')[1]?.split('?')[0];
    return {
      type: 'vimeo',
      url: `https://player.vimeo.com/video/${id}`,
    };
  }

  // ❌ Unknown / not embeddable
  return {
    type: 'external',
    url,
  };
}
export const handleScrollToTop = (): void => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

/**
 * Reverse-engineers a slug (generated via generateUniqueSlug: slugify(name) + optional '-N' suffix)
 * back into a human-readable, title-cased label for UI display in filter chips.
 */
export const unslugifyCategorySlug = (slug: string): string => {
  if (!slug) return '';
  // Strip unique numeric suffix added by generateUniqueSlug (e.g. "-1", "-2")
  const baseSlug = String(slug).replace(/-\d+$/, '');
  // Replace hyphens and underscores with spaces
  const words = baseSlug.split(/[-_]+/);
  // Capitalize each word
  return words
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};
